import { Autocomplete, Box, Button, CircularProgress, Icon, MenuItem, Paper, Select, TextField, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { Environment } from '../../environment';
import { useDebounce } from '../../hooks';
import { FazendaService } from '../../services/api/cadastros/FazendaService';

interface IDadosSelect {
  nameBD: string;
  descricao: string;
}

type TAutoCompleteOption = {
  idfazenda: string;
  label: string;
}

interface IFerramentasDaListagemProps {
  defaultValueFazenda?: string;
  mostrarInputFazenda?: boolean;
  aoMudarFazenda?: (novoTexto: string) => void;
  textoDaBusca?: string;
  aoMudarTextoDeBusca?: (novoTexto: string) => void;
  dadosSelecao?: IDadosSelect[];
  dataInicial?: string;
  dataFinal?: string;
  mostrarInputData?: boolean;
  mostrarInputBusca?: boolean;
  aoMudarDataInicial?: (novoTexto: string) => void;
  aoMudarDataFinal?: (novoTexto: string) => void;
  textoBotaoNovo?: string;
  mostrarBotaoNovo?: boolean;
  aoClicarEmNovo?: () => void;
  mostrarInputBasicSelect?: boolean
  textoDoSelect?: string;
  aoMudarTextoDoSelect?: (novoTextoselect: string) => void;
}
export const FerramentasDaListagemOS: React.FC<IFerramentasDaListagemProps> = ({
  defaultValueFazenda = '',
  aoMudarFazenda,
  mostrarInputFazenda = false,
  textoDaBusca = '',
  dataInicial = '',
  aoMudarDataInicial,
  dataFinal = '',
  aoMudarDataFinal,
  aoMudarTextoDeBusca,
  mostrarInputData = false,
  mostrarInputBusca = false,
  aoClicarEmNovo,
  textoBotaoNovo = 'Novo',
  mostrarBotaoNovo = true,
  dadosSelecao = [],
  mostrarInputBasicSelect = false,
  textoDoSelect = '',
  aoMudarTextoDoSelect,
}) => {
  const theme = useTheme();
  const partesDaDataInicial = dataInicial.split('/');
  const dataInicialFormatada = partesDaDataInicial[2] + '-' + partesDaDataInicial[1] + '-' + partesDaDataInicial[0];
  const partesDaDataFinal = dataFinal.split('/');
  const dataFinalFormatada = partesDaDataFinal[2] + '-' + partesDaDataFinal[1] + '-' + partesDaDataFinal[0];
  const { debounce } = useDebounce();
  const [selectedId, setSelectedId] = useState<string | undefined>(defaultValueFazenda);
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    setSelectedId(defaultValueFazenda);
  }, [defaultValueFazenda, textoDoSelect]);

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      FazendaService.getAlllist(selectedId, busca)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            // alert(result.message);
          } else {
            setOpcoes(result.data.map(fazenda => ({ idfazenda: fazenda.idfazenda, label: fazenda.descricao })));
          }
        });
    });
  }, [busca, selectedId]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(opcao => opcao.idfazenda === selectedId);
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);

  return (
    <Box
      marginX={2}
      padding={1}
      paddingX={2}
      display="flex"
      alignItems="center"
      component={Paper}
    >
      {mostrarInputBasicSelect && (
        <Select
          size="small" sx={{ minWidth: 150 }}
          value={textoDoSelect}
          onChange={(event) => aoMudarTextoDoSelect?.(event.target.value)}
        >
          {dadosSelecao.map((item) => (
            <MenuItem key={item.nameBD} value={item.nameBD}>
              {item.descricao}
            </MenuItem>
          ))}
        </Select>
      )}

      {mostrarInputFazenda && (
        <Autocomplete
          sx={{ minWidth: 250 }}
          openText='Abrir'
          closeText='Fechar'
          noOptionsText='Sem opções'
          loadingText='Carregando...'
          disablePortal
          options={opcoes}
          loading={isLoading}
          value={autoCompleteSelectedOption}
          onInputChange={(_, newValue) => setBusca(newValue)}
          onChange={(_, newValue) => { setSelectedId(newValue?.idfazenda); aoMudarFazenda?.(newValue?.idfazenda || ''); setBusca(''); }}
          popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              label="Fazenda"
            />
          )}
        />
      )}

      {mostrarInputBusca && (
        <TextField
          size="small"
          sx={{ marginLeft:1, minWidth: 150 }}
          value={textoDaBusca}
          placeholder={Environment.INPUT_DE_BUSCA}
          onChange={(e) => aoMudarTextoDeBusca?.(e.target.value)}
        />
      )}

      {mostrarInputData && (

        <LocalizationProvider dateAdapter={AdapterDayjs}>

          <DatePicker
            sx={{marginLeft:1, minWidth: 150, maxWidth: 150 }}
            label="Data Inicial"
            format='DD/MM/YYYY'
            defaultValue={dayjs()}
            value={dayjs(dataInicialFormatada)}
            onChange={(date: Dayjs | null) => {
              const novoTexto = date ? date.format('DD/MM/YYYY') : '';
              aoMudarDataInicial?.(novoTexto);
            }}
            slotProps={{
              textField: {    
                size:'small',
              }
            }}
          />

          <DatePicker
            sx={{ marginLeft:1, minWidth: 150, maxWidth: 150 }}
            label="Data Final"
            format='DD/MM/YYYY'
            defaultValue={dayjs()}
            value={dayjs(dataFinalFormatada)}
            onChange={(date: Dayjs | null) => {
              const novoTexto = date ? date.format('DD/MM/YYYY') : '';
              aoMudarDataFinal?.(novoTexto);
            }}
            slotProps={{
              textField: {    
                size:'small',
              }
            }}
          />
        </LocalizationProvider>
      )}


      <Box flex={1} display="flex" justifyContent="end">
        {mostrarBotaoNovo && (
          <Button
            size='small'
            color='primary'
            disableElevation
            variant='contained'
            onClick={aoClicarEmNovo}
            endIcon={<Icon>add</Icon>}
          >{textoBotaoNovo}</Button>
        )}
      </Box>
    </Box>
  );
};